import { type ComponentPropsWithoutRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Modal from '@/components/Modal';
import { BlockButton } from '@/components/BlockButton';
import clsx from 'clsx';
import {
  useBulkProgress,
  useBulkWithdraw,
  useBulkReset,
  useBulkDestroy,
} from '../api/useBulkJobPostEndpointActions';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';

export type StatusChangeType = 'toSuggest' | 'toSelection' | 'toWithdraw' | 'toDelete';

type Props = {
  modal: Omit<ComponentPropsWithoutRef<typeof Modal>, 'children'>;
  close: () => void;
  statusChangeType: StatusChangeType;
  providedJobPostId: number;
  candidateIds: number[];
  onSuccess?: () => void;
};

export default function SuggestedCandidateStatusChangeModal({
  modal,
  close,
  statusChangeType,
  providedJobPostId,
  candidateIds,
  onSuccess,
}: Props) {
  const { mutateAsync: mutateProgress } = useBulkProgress();
  const { mutateAsync: mutateWithdraw } = useBulkWithdraw();
  const { mutateAsync: mutateReset } = useBulkReset();
  const { mutateAsync: mutateDestroy } = useBulkDestroy();
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const queryClient = useQueryClient();

  const handleStatusChange = async () => {
    try {
      if (statusChangeType === 'toWithdraw') {
        await mutateWithdraw({ jobPostId: providedJobPostId, candidateIds });
      } else if (statusChangeType === 'toSelection') {
        await mutateProgress({ jobPostId: providedJobPostId, candidateIds });
      } else if (statusChangeType === 'toSuggest') {
        await mutateReset({ jobPostId: providedJobPostId, candidateIds });
      } else if (statusChangeType === 'toDelete') {
        await mutateDestroy({ jobPostId: providedJobPostId, candidateIds });
      }

      if (onSuccess) {
        onSuccess();
      }

      showSuccessToast(
        statusChangeType === 'toDelete' ? '削除が完了しました' : '変更が完了しました'
      );
      await sleep(1000);
      queryClient.invalidateQueries({ queryKey: ['suggestedCandidates', providedJobPostId] });
      queryClient.invalidateQueries({ queryKey: ['providedJobPost', providedJobPostId] });
      close();
    } catch (error) {
      showErrorToast(
        statusChangeType === 'toDelete' ? '削除に失敗しました' : 'ステータス変更に失敗しました'
      );
    }
  };

  const getModalConfig = () => {
    switch (statusChangeType) {
      case 'toSuggest':
        return {
          title: '変更の確認',
          confirmText: '提案へ戻す',
          confirmButtonWidth: 'w-[94px]',
          message: 'ステータスを「提案中」に変更しますか？',
        };
      case 'toSelection':
        return {
          title: '変更の確認',
          confirmText: '選考へ進む',
          confirmButtonWidth: 'w-[94px]',
          message: 'ステータスを「選考」に変更しますか？',
        };
      case 'toWithdraw':
        return {
          title: '変更の確認',
          confirmText: '取り下げ',
          confirmButtonWidth: 'w-[80px]',
          message: 'ステータスを「取り下げ」に変更しますか？',
        };
      case 'toDelete':
        return {
          title: '削除の確認',
          confirmText: '削除',
          confirmButtonWidth: 'w-[80px]',
          message: 'この提案求人を完全に削除します。この操作は元に戻せません。',
        };
    }
  };

  const { title, confirmText, confirmButtonWidth, message } = getModalConfig();

  return (
    <Modal {...modal}>
      <div className="w-[640px] rounded bg-white">
        <div className="flex h-[56px] items-center border-b border-solid border-border px-6">
          <p className="text-base font-bold">{title}</p>
        </div>
        <div className="p-6">
          <p className="text-base">{message}</p>
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 border-t border-solid border-border px-6">
          <div className="w-[94px]">
            <BlockButton type="button" text="キャンセル" colorType="white" onClick={close} />
          </div>
          <div className={clsx(confirmButtonWidth)}>
            <BlockButton
              type="button"
              text={confirmText}
              colorType={
                statusChangeType === 'toWithdraw' || statusChangeType === 'toDelete'
                  ? 'red'
                  : 'blue'
              }
              onClick={handleStatusChange}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
